import React from 'react';

// Tokenize the highlight string on whitespace; each token is matched
// independently so multi-word inputs (e.g. "athena aur") still mark the parts
// that did match. For tokens without a substring match, fall back to the
// longest common prefix of any word in the text — this keeps fuzzy/trigram
// backend matches like "aur" → "Auer" visibly highlighted on the "Au" prefix.
const Highlight = ({ text, highlight }) => {
  if (!highlight) return text;
  if (!text) return null;

  const textStr = String(text);
  const tokens = String(highlight).toLowerCase().split(/\s+/).filter(Boolean);
  if (!tokens.length) return textStr;

  const lowerText = textStr.toLowerCase();
  const wordSpans = [];
  for (let i = 0; i < lowerText.length; ) {
    while (i < lowerText.length && /\s/.test(lowerText[i])) i++;
    const start = i;
    while (i < lowerText.length && !/\s/.test(lowerText[i])) i++;
    if (start < i) wordSpans.push([start, i]);
  }

  const ranges = [];
  for (const token of tokens) {
    let from = 0;
    let matched = false;
    let idx;
    while ((idx = lowerText.indexOf(token, from)) !== -1) {
      ranges.push([idx, idx + token.length]);
      from = idx + token.length;
      matched = true;
    }
    if (matched) continue;
    if (token.length < 2) continue;
    let bestLen = 0;
    let bestStart = -1;
    for (const [ws, we] of wordSpans) {
      let p = 0;
      while (p < we - ws && p < token.length && lowerText[ws + p] === token[p]) p++;
      if (p > bestLen) {
        bestLen = p;
        bestStart = ws;
      }
    }
    if (bestLen >= 2) ranges.push([bestStart, bestStart + bestLen]);
  }

  if (!ranges.length) return textStr;

  ranges.sort((a, b) => a[0] - b[0]);
  const merged = [ranges[0]];
  for (let k = 1; k < ranges.length; k++) {
    const last = merged[merged.length - 1];
    if (ranges[k][0] <= last[1]) last[1] = Math.max(last[1], ranges[k][1]);
    else merged.push(ranges[k]);
  }

  const segments = [];
  let pos = 0;
  merged.forEach(([s, e], i) => {
    if (pos < s) segments.push({ text: textStr.slice(pos, s), hl: false, key: `p${i}` });
    segments.push({ text: textStr.slice(s, e), hl: true, key: `h${i}` });
    pos = e;
  });
  if (pos < textStr.length) segments.push({ text: textStr.slice(pos), hl: false, key: 'pEnd' });

  return (
    <>
      {segments.map((seg) =>
        seg.hl ? (
          <span key={seg.key} className="!bg-warning-100">
            {seg.text}
          </span>
        ) : (
          <React.Fragment key={seg.key}>{seg.text}</React.Fragment>
        )
      )}
    </>
  );
};

export default Highlight;
